declare const Chart: any;
import AccountManager from './accountManager.js';
import DashboardLayout from './dashboardLayout.js';
import d from './DOM.js';

export default class Dashboard {
	static equityChart: any = null;
	static monthlyChart: any = null;
	static symbolChart: any = null;
	static dayChart: any = null;
	
	/**
	 * `date` minus `months` calendar months, clamped to the target month's last day so the 31st
	 * can't roll forward into the following month (31 Mar − 2 → 31 Jan, but 30 Apr − 2 → 28 Feb).
	 */
	static monthsBefore(date: Date, months: number): Date {
		const target = new Date(date.getFullYear(), date.getMonth() - months, 1);
		const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
		target.setDate(Math.min(date.getDate(), lastDay));
		return target;
	}

	static init() {
		// Date Range
		const startInput = d.qs<HTMLInputElement>('#dashboard-start-date');
		const endInput = d.qs<HTMLInputElement>('#dashboard-end-date');
		const applyBtn = d.qs<HTMLButtonElement>('#dashboard-apply-date');
		
		if (startInput && endInput && applyBtn) {
			// Set defaults (last two months to Today). Year-to-date meant the first paint grew a
			// month heavier every month — /api/dashboard/metrics groups every deal in range and
			// re-derives the curve — so the landing view is now a fixed two-month window and the
			// picker is there for anyone who wants further back.
			const today = new Date();
			const start = Dashboard.monthsBefore(today, 2);

			// Format YYYY-MM-DD from LOCAL parts. toISOString() is UTC, so ahead-of-UTC zones
			// (e.g. UTC+8 before 08:00) stamped "yesterday" and silently dropped today's trades.
			const fmt = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

			startInput.value = fmt(start);
			endInput.value = fmt(today);

			applyBtn.addEventListener('click', () => Dashboard.loadMetrics());
		}
		
		// Card visibility/order (Customise). The skill badge + "Share My Stats" moved to /level.
		DashboardLayout.init();
		
		Dashboard.loadMetrics();
	}
	
	static async loadMetrics() {
		try {
			if (!AccountManager.currentAccount) { return; }
			
			const startInput = d.qs<HTMLInputElement>('#dashboard-start-date');
			const endInput = d.qs<HTMLInputElement>('#dashboard-end-date');
			
			let query = `?accountNumber=${AccountManager.currentAccount.accountNumber}`;
			if (startInput && endInput) { query += `&startDate=${startInput.value}&endDate=${endInput.value}`; }
			
			const res = await fetch(`/api/dashboard/metrics${query}`);
			const data = await res.json();
			
			if (data.error) {
				console.error(data.error);
				return;
			}
			
			Dashboard.renderKPIs(data.overall, data.risk);
			Dashboard.renderSkillMetrics(data.skill);
			Dashboard.renderCharts(data.equityCurve, data.timeBased.monthly, data.insights.bySymbol, data.timeBased.dayOfWeek, data.overall.netProfit);
			Dashboard.renderInsights(data.insights, data.risk);
			Dashboard.renderLongShort(data.insights.longShort);
		} catch (err) {
			console.error('Failed to load dashboard metrics', err);
		}
	}
	
	static renderKPIs(overall: any, risk: any) {
		const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
		const fmtNum = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n);
		
		d.qs<HTMLElement>('#kpi-net-profit').textContent = fmtMoney(overall.netProfit);
		d.qs<HTMLElement>('#kpi-net-profit').style.color = overall.netProfit >= 0 ? 'var(--theme-status-profit)' : 'var(--theme-status-loss)';
		d.qs<HTMLElement>('#kpi-profit-factor').textContent = `PF: ${fmtNum(overall.profitFactor)}`;
		d.qs<HTMLElement>('#kpi-win-rate').textContent = `${fmtNum(overall.winRate)}%`;
		d.qs<HTMLElement>('#kpi-total-trades').textContent = `${overall.totalTrades} Trades`;
		d.qs<HTMLElement>('#kpi-avg-rr').textContent = fmtNum(overall.avgRR);
		d.qs<HTMLElement>('#kpi-expectancy-main').textContent = fmtMoney(overall.expectancy);
		const fmtPct = (n: number | null | undefined) => (n === null || n === undefined ? '--%' : `${fmtNum(n)}%`);
		const signColor = (n: number) => n >= 0 ? 'var(--theme-status-profit)' : 'var(--theme-status-loss)';
		
		d.qs<HTMLElement>('#kpi-max-dd').textContent = fmtMoney(risk.maxDrawdown);
		d.qs<HTMLElement>('#kpi-max-dd-percent').textContent = fmtPct(risk.maxDrawdownPercent);
		
		// % first alternatives of Net Profit / Max Drawdown (same figures, headline swapped)
		const netPct = d.qs<HTMLElement>('#kpi-net-profit-pct');
		netPct.textContent = overall.netProfitPercent === null || overall.netProfitPercent === undefined
			? '--%'
			: `${overall.netProfitPercent >= 0 ? '+' : ''}${fmtNum(overall.netProfitPercent)}%`;
		netPct.style.color = signColor(overall.netProfit);
		d.qs<HTMLElement>('#kpi-net-profit-pct-sub').textContent = fmtMoney(overall.netProfit);
		d.qs<HTMLElement>('#kpi-max-dd-pct').textContent = fmtPct(risk.maxDrawdownPercent);
		d.qs<HTMLElement>('#kpi-max-dd-pct-sub').textContent = fmtMoney(risk.maxDrawdown);
		
		// Profit factor, Sharpe, average monthly return
		d.qs<HTMLElement>('#kpi-profit-factor-main').textContent = fmtNum(overall.profitFactor);
		d.qs<HTMLElement>('#kpi-profit-factor-main').style.color = overall.profitFactor >= 1 ? 'var(--theme-status-profit)' : 'var(--theme-status-loss)';
		const sharpe = d.qs<HTMLElement>('#kpi-sharpe');
		sharpe.textContent = overall.sharpeRatio === null || overall.sharpeRatio === undefined ? '--' : fmtNum(overall.sharpeRatio);
		sharpe.title = overall.sharpeRatio === null ? 'Needs at least 5 trading days and a known account balance' : '';
		const avgMonthly = d.qs<HTMLElement>('#kpi-avg-monthly-return');
		avgMonthly.textContent = overall.avgMonthlyReturnPercent === null || overall.avgMonthlyReturnPercent === undefined
			? '--%'
			: `${overall.avgMonthlyReturnPercent >= 0 ? '+' : ''}${fmtNum(overall.avgMonthlyReturnPercent)}%`;
		if (overall.avgMonthlyReturnPercent !== null && overall.avgMonthlyReturnPercent !== undefined) { avgMonthly.style.color = signColor(overall.avgMonthlyReturnPercent); }
		d.qs<HTMLElement>('#kpi-avg-monthly-return-sub').textContent = `${fmtMoney(overall.avgMonthlyPnl ?? 0)} / month`;
        
        // New Metrics
        d.qs<HTMLElement>('#stat-win-streak-main').textContent = risk.maxWinStreak;
        d.qs<HTMLElement>('#stat-loss-streak-main').textContent = risk.maxLossStreak;
	}
	
	static renderCharts(equityCurve: any[], monthlyData: any[], symbolData: any[], dayData: any[], totalPeriodPnL: number = 0) {
		const initialBalance = AccountManager.currentAccount?.balance ? AccountManager.currentAccount.balance - totalPeriodPnL : 0;
		let totalReturnPercent = 0;
		let returnPoints = equityCurve.map(() => 0);
		
		if (initialBalance > 0) {
			totalReturnPercent = (totalPeriodPnL / initialBalance) * 100;
			returnPoints = equityCurve.map(p => (p.balance / initialBalance) * 100);
		}

		const returnStatEl = d.qs<HTMLElement>('#equity-return-stat');
		if (returnStatEl) {
			const sign = totalReturnPercent >= 0 ? '+' : '';
			returnStatEl.textContent = `Total Return: ${sign}${totalReturnPercent.toFixed(2)}%`;
			returnStatEl.style.color = totalReturnPercent >= 0 ? 'var(--theme-status-profit)' : 'var(--theme-status-loss)';
		}

		const formatDate = (dateStr: string) => {
			const date = new Date(dateStr);
			const format = (window as any).user?.dateFormat || 'DD/MM/YYYY';
			const day = String(date.getDate()).padStart(2, '0');
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const year = date.getFullYear();
			
			if (format === 'MM/DD/YYYY') { return `${month}/${day}/${year}`; }
			return `${day}/${month}/${year}`;
		};
		
		// Equity Chart
		const ctxEquity = d.qs<HTMLCanvasElement>('#equityChart').getContext('2d');
		if (Dashboard.equityChart) { Dashboard.equityChart.destroy(); }
		
		Dashboard.equityChart = new Chart(ctxEquity, {
			type: 'line',
			data: {
				labels: equityCurve.map(p => formatDate(p.date)),
				datasets: [{
					label: 'Equity',
					data: equityCurve.map(p => p.balance),
					borderColor: '#C4CED8', // brand-silver
					backgroundColor: 'rgba(196, 206, 216, 0.1)',
					tension: 0.1,
					fill: true,
					returnPoints: returnPoints
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: { 
					legend: { display: false },
					tooltip: {
						callbacks: {
							title: (context: any) => {
								return context[0].label;
							},
							label: (context: any) => {
								const value = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(context.parsed.y);
								const dataset = context.dataset;
								const index = context.dataIndex;
								const returnPct = dataset.returnPoints && dataset.returnPoints[index] !== undefined ? dataset.returnPoints[index] : 0;
								const sign = returnPct >= 0 ? '+' : '';
								return `Equity: ${value} (${sign}${returnPct.toFixed(2)}%)`;
							}
						}
					}
				},
				scales: {
					x: { display: false },
					y: { grid: { color: '#1E293B' } }
				}
			}
		});
		
		// Monthly Chart
		const ctxMonthly = d.qs<HTMLCanvasElement>('#monthlyChart').getContext('2d');
		if (Dashboard.monthlyChart) { Dashboard.monthlyChart.destroy(); }
		
		Dashboard.monthlyChart = new Chart(ctxMonthly, {
			type: 'bar',
			data: {
				labels: monthlyData.map(m => m.month),
				datasets: [{
					label: 'PnL',
					data: monthlyData.map(m => m.pnl),
					backgroundColor: monthlyData.map(m => m.pnl >= 0 ? '#10B981' : '#EF4444'),
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: { legend: { display: false } },
				scales: {
					y: { grid: { color: '#1E293B' } }
				}
			}
		});

		// Symbol Chart
		const ctxSymbol = d.qs<HTMLCanvasElement>('#symbolChart').getContext('2d');
		if (Dashboard.symbolChart) { Dashboard.symbolChart.destroy(); }
		
		const topSymbols = symbolData.slice(0, 10); // Show top 10
		Dashboard.symbolChart = new Chart(ctxSymbol, {
			type: 'bar',
			data: {
				labels: topSymbols.map(s => s.symbol),
				datasets: [{
					label: 'PnL',
					data: topSymbols.map(s => s.pnl),
					backgroundColor: topSymbols.map(s => s.pnl >= 0 ? '#10B981' : '#EF4444'),
				}]
			},
			options: {
				indexAxis: 'y',
				responsive: true,
				maintainAspectRatio: false,
				plugins: { legend: { display: false } },
				scales: {
					x: { grid: { color: '#1E293B' } }
				}
			}
		});

		// Day of Week Chart
		const ctxDay = d.qs<HTMLCanvasElement>('#dayChart').getContext('2d');
		if (Dashboard.dayChart) { Dashboard.dayChart.destroy(); }
		
		const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
		const dayPerf = days.map(day => {
			const found = dayData.find(d => d.day === day);
			return found ? found.pnl : 0;
		});

		Dashboard.dayChart = new Chart(ctxDay, {
			type: 'bar',
			data: {
				labels: days,
				datasets: [{
					label: 'PnL',
					data: dayPerf,
					backgroundColor: dayPerf.map(p => p >= 0 ? '#10B981' : '#EF4444'),
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: { legend: { display: false } },
				scales: {
					y: { grid: { color: '#1E293B' } }
				}
			}
		});
	}
	
	static renderInsights(insights: any, risk: any) {
		const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
		const fmtNum = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n);
        
        // Format Time Duration
        const formatDuration = (ms: number) => {
            if (!ms) return '0m';
            const minutes = Math.floor(ms / 60000);
            const hours = Math.floor(minutes / 60);
            if (hours > 0) {
                 const mins = minutes % 60;
                 return `${hours}h ${mins}m`;
            }
            return `${minutes}m`;
        };
        
        const avgTimeElem = d.qs<HTMLElement>('#kpi-avg-time');
        if (avgTimeElem) avgTimeElem.textContent = formatDuration(insights.avgTradeTime);
		
		// Strategy List
		const stratList = d.qs<HTMLElement>('#strategy-list');
		stratList.innerHTML = '';
		
		insights.byStrategy.forEach((s: any) => {
			const div = document.createElement('div');
			div.className = 'list-item';
			div.innerHTML = `
				<span>${s.strategy}</span>
				<span style="color: ${s.pnl >= 0 ? 'var(--theme-status-profit)' : 'var(--theme-status-loss)'}">
					${fmtMoney(s.pnl)} (${fmtNum(s.winRate)}%)
				</span>
			`;
			stratList.appendChild(div);
		});
		
		// Journal Stats
		if(d.qs<HTMLElement>('#stat-journaled')) d.qs<HTMLElement>('#stat-journaled').textContent = `${fmtNum(insights.journaledPercent)}%`;
		if(d.qs<HTMLElement>('#stat-mood')) d.qs<HTMLElement>('#stat-mood').textContent = insights.commonMood;
	}
	
	static renderSkillMetrics(skill: any) {
		const fmtNum = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
		
		d.qs<HTMLElement>('#skill-plan-adherence').textContent = `${fmtNum(skill.planAdherence)}%`;
		d.qs<HTMLElement>('#skill-confidence').textContent = `${fmtNum(skill.avgConfidence)}/10`;
		d.qs<HTMLElement>('#skill-negative-mood').textContent = `${fmtNum(skill.negativeMoodPercent)}%`;
		d.qs<HTMLElement>('#skill-months-active').textContent = `${fmtNum(skill.monthsActive)} Mo`;
		
		// Badge (lives on the /level page now; kept here in case a layout still carries it)
		if (!d.qs<HTMLElement>('#badge-level')) { return; }
		d.qs<HTMLElement>('#badge-level').textContent = skill.level;
		d.qs<HTMLElement>('#badge-score').textContent = `Skill Score: ${Math.round(skill.score)}/100`;
		d.qs<HTMLElement>('#badge-icon').textContent = Math.round(skill.score).toString();
		
		// Color code the badge icon
		const icon = d.qs<HTMLElement>('#badge-icon');
		icon.style.background = Dashboard.badgeGradient(skill.level);
	}
	
	/** Badge colour per skill level (shared with the /level page). */
	static badgeGradient(level: string): string {
		if (level === 'Advanced') { return 'linear-gradient(135deg, #8B5CF6, #6D28D9)'; }
		if (level === 'Skilled') { return 'linear-gradient(135deg, #10B981, #059669)'; }
		if (level === 'Competent') { return 'linear-gradient(135deg, #3B82F6, #2563EB)'; }
		if (level === 'Developing') { return 'linear-gradient(135deg, #F59E0B, #D97706)'; }
		return 'linear-gradient(135deg, #64748B, #475569)';
	}

	static renderLongShort(data: any) {
		const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
		
		const longPnlElem = d.qs<HTMLElement>('#long-pnl');
		const shortPnlElem = d.qs<HTMLElement>('#short-pnl');
		const longBar = d.qs<HTMLElement>('#long-bar');
		const shortBar = d.qs<HTMLElement>('#short-bar');

		longPnlElem.textContent = fmtMoney(data.long.pnl);
		longPnlElem.style.color = data.long.pnl >= 0 ? 'var(--theme-status-profit)' : 'var(--theme-status-loss)';
		
		shortPnlElem.textContent = fmtMoney(data.short.pnl);
		shortPnlElem.style.color = data.short.pnl >= 0 ? 'var(--theme-status-profit)' : 'var(--theme-status-loss)';

		// Calculate ratio for bars
		const totalAbs = Math.abs(data.long.pnl) + Math.abs(data.short.pnl);
		if (totalAbs > 0) {
			const longPct = (Math.abs(data.long.pnl) / totalAbs) * 100;
			const shortPct = (Math.abs(data.short.pnl) / totalAbs) * 100;
			longBar.style.width = `${longPct}%`;
			shortBar.style.width = `${shortPct}%`;
		} else {
			longBar.style.width = '50%';
			shortBar.style.width = '50%';
		}
	}
}
